import { useState } from "react";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Loader2 } from "lucide-react";
import type { InactiveReason } from "../../types";

interface InactivateMinisterDialogProps {
  open: boolean;
  ministerName: string;
  reasons: InactiveReason[];
  onConfirm: (reasonCode: string, eventDate: string, notes?: string) => Promise<void>;
  onCancel: () => void;
}

export function InactivateMinisterDialog({
  open,
  ministerName,
  reasons,
  onConfirm,
  onCancel,
}: InactivateMinisterDialogProps) {
  const todayIso = new Date().toISOString().split("T")[0];

  const [reasonCode, setReasonCode] = useState<string>("");
  const [eventDate, setEventDate] = useState<string>(todayIso);
  const [notes, setNotes] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [reasonError, setReasonError] = useState(false);

  const handleConfirm = async () => {
    if (!reasonCode) {
      setReasonError(true);
      return;
    }
    setIsSubmitting(true);
    try {
      await onConfirm(reasonCode, eventDate, notes.trim() || undefined);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (!open && !isSubmitting) {
      resetAndCancel();
    }
  };

  const resetAndCancel = () => {
    setReasonCode("");
    setEventDate(todayIso);
    setNotes("");
    setReasonError(false);
    onCancel();
  };

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogContent className="max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle>Inactivar Ministro</AlertDialogTitle>
          <AlertDialogDescription>
            Marcar a <span className="font-medium text-foreground">{ministerName}</span> como inactivo.
            Esta acción queda registrada en el historial.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-4 py-2">
          {/* Reason (mandatory) */}
          <div className="space-y-1.5">
            <Label htmlFor="inactive-reason">
              Motivo <span className="text-destructive">*</span>
            </Label>
            <Select
              value={reasonCode}
              onValueChange={(val) => {
                setReasonCode(val);
                setReasonError(false);
              }}
            >
              <SelectTrigger id="inactive-reason" className={reasonError ? "border-destructive" : ""}>
                <SelectValue placeholder="Seleccionar motivo..." />
              </SelectTrigger>
              <SelectContent>
                {reasons.map((r) => (
                  <SelectItem key={r.code} value={r.code}>
                    {r.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {reasonError && (
              <p className="text-sm text-destructive">El motivo es obligatorio.</p>
            )}
          </div>

          {/* Date */}
          <div className="space-y-1.5">
            <Label htmlFor="inactive-date">Fecha</Label>
            <Input
              id="inactive-date"
              type="date"
              value={eventDate}
              onChange={(e) => setEventDate(e.target.value)}
              max={todayIso}
            />
          </div>

          {/* Notes (optional) */}
          <div className="space-y-1.5">
            <Label htmlFor="inactive-notes">Notas (opcional)</Label>
            <Textarea
              id="inactive-notes"
              placeholder="Observaciones adicionales..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
            />
          </div>
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel onClick={resetAndCancel} disabled={isSubmitting}>
            Cancelar
          </AlertDialogCancel>
          <Button variant="destructive" onClick={handleConfirm} disabled={isSubmitting}>
            {isSubmitting && <Loader2 className="mr-2 size-4 animate-spin" />}
            Inactivar
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
